// Small shared building blocks for the docs pages, so every page reads the
// same: body copy, inline code, lists, and a titled card section. Pages
// compose these with the app's Card/Alert/Table kit and <CommandLine>.

import type { ReactNode } from "react";
import type { Icon } from "@tabler/icons-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

/** Inline code, matching the quick-start idiom. */
export function K({ children }: { children: ReactNode }) {
  return <code className="font-mono">{children}</code>;
}

/** A body paragraph. */
export function P({ children }: { children: ReactNode }) {
  return <p className="text-sm leading-relaxed text-muted-foreground">{children}</p>;
}

/** A bulleted list of short points. */
export function Points({ children }: { children: ReactNode }) {
  return (
    <ul className="ml-5 flex list-disc flex-col gap-1.5 text-sm leading-relaxed text-muted-foreground">
      {children}
    </ul>
  );
}

/** A titled section card: the docs' repeating unit. */
export function DocSection({
  icon: SectionIcon,
  title,
  description,
  children,
}: {
  icon?: Icon;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {SectionIcon && <SectionIcon className="size-4" />} {title}
        </CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">{children}</CardContent>
    </Card>
  );
}
